import { useEffect, useRef, useState } from 'react';
import { aiserverbaselink } from '../../../Env_Variables';
import { aiInstituteHeaders } from './aiInstituteHeaders';
import { executeDashboardTool } from './aiTools';
import { usageFooterText } from './usageDisplay';

function sendEvent(dc, event) {
    if (!dc || dc.readyState !== 'open') return;
    dc.send(JSON.stringify(event));
}

function waitForIceGathering(pc, timeoutMs = 4000) {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
        const done = () => {
            pc.removeEventListener('icegatheringstatechange', onChange);
            clearTimeout(timer);
            resolve();
        };
        const onChange = () => {
            if (pc.iceGatheringState === 'complete') done();
        };
        const timer = setTimeout(done, timeoutMs);
        pc.addEventListener('icegatheringstatechange', onChange);
    });
}

function priceRealtimeUsages(usages, model) {
    const list = Array.isArray(usages) ? usages : [];
    let input_text = 0;
    let input_text_cached = 0;
    let input_audio = 0;
    let input_audio_cached = 0;
    let output_text = 0;
    let output_audio = 0;
    let total_tokens = 0;

    for (const usage of list) {
        if (!usage) continue;
        total_tokens += Number(usage.total_tokens) || 0;
        const inDetails = usage.input_token_details || {};
        const outDetails = usage.output_token_details || {};
        const cached = inDetails.cached_tokens_details || {};
        input_text += Number(inDetails.text_tokens) || 0;
        input_audio += Number(inDetails.audio_tokens) || 0;
        input_text_cached += Number(cached.text_tokens) || 0;
        input_audio_cached += Number(cached.audio_tokens) || 0;
        output_text += Number(outDetails.text_tokens) || 0;
        output_audio += Number(outDetails.audio_tokens) || 0;
    }

    // gpt-realtime-mini defaults (USD / 1M tokens)
    const isMini = String(model || '').includes('mini');
    const rates = isMini
        ? { ti: 0.6, tic: 0.3, to: 2.4, ai: 10, aic: 0.3, ao: 20 }
        : { ti: 4, tic: 0.4, to: 16, ai: 32, aic: 0.4, ao: 64 };

    const billableTextIn = Math.max(0, input_text - input_text_cached);
    const billableAudioIn = Math.max(0, input_audio - input_audio_cached);
    const cost =
        (billableTextIn / 1e6) * rates.ti +
        (input_text_cached / 1e6) * rates.tic +
        (billableAudioIn / 1e6) * rates.ai +
        (input_audio_cached / 1e6) * rates.aic +
        (output_text / 1e6) * rates.to +
        (output_audio / 1e6) * rates.ao;

    return {
        model: model || 'gpt-realtime-mini',
        total_tokens,
        input_text_tokens: input_text,
        input_audio_tokens: input_audio,
        output_text_tokens: output_text,
        output_audio_tokens: output_audio,
        cost_usd: Number(cost.toFixed(8)),
        cost_usd_display: `$${cost.toFixed(6)}`,
    };
}

export function CallIcon({ size = 18 }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.2 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1L6.6 10.8z"
                fill="currentColor"
            />
        </svg>
    );
}

function stopStatusSpeech() {
    /* status is caption-only — no second browser voice */
}

function speakStatus(_text) {
    /* intentionally no-op: OpenAI voice only (avoids dual-voice overlap) */
}

/**
 * Mobile-style dual-tone ring (440+480Hz) until the realtime session is ready.
 * No audio asset needed — Web Audio API.
 */
function createCallRingtone() {
    let audioCtx = null;
    let timer = null;
    let stopped = true;
    let activeNodes = [];

    const clearNodes = () => {
        activeNodes.forEach((node) => {
            try {
                node.stop?.();
            } catch (_) {
                /* ignore */
            }
            try {
                node.disconnect?.();
            } catch (_) {
                /* ignore */
            }
        });
        activeNodes = [];
    };

    const playBurst = (durationSec = 0.4) => {
        if (stopped || !audioCtx) return;
        const now = audioCtx.currentTime;
        const master = audioCtx.createGain();
        master.connect(audioCtx.destination);
        master.gain.setValueAtTime(0.0001, now);
        master.gain.exponentialRampToValueAtTime(0.14, now + 0.025);
        master.gain.setValueAtTime(0.14, now + Math.max(0.05, durationSec - 0.06));
        master.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
        activeNodes.push(master);

        [440, 480].forEach((freq) => {
            const osc = audioCtx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = freq;
            osc.connect(master);
            osc.start(now);
            osc.stop(now + durationSec + 0.02);
            activeNodes.push(osc);
        });
    };

    const scheduleLoop = () => {
        if (stopped) return;
        // Double ring, then pause — like a phone
        playBurst(0.4);
        timer = setTimeout(() => {
            if (stopped) return;
            playBurst(0.4);
            timer = setTimeout(() => {
                if (stopped) return;
                timer = setTimeout(scheduleLoop, 900);
            }, 450);
        }, 450);
    };

    return {
        async start() {
            this.stop();
            stopped = false;
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            audioCtx = new AC();
            try {
                if (audioCtx.state === 'suspended') await audioCtx.resume();
            } catch (_) {
                /* ignore */
            }
            scheduleLoop();
        },
        stop() {
            stopped = true;
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            clearNodes();
            if (audioCtx) {
                try {
                    audioCtx.close();
                } catch (_) {
                    /* ignore */
                }
                audioCtx = null;
            }
        },
    };
}

function statusPhraseForTools(toolCalls) {
    const names = (toolCalls || []).map((c) => c.name);
    if (names.includes('update_product_info')) return 'بحدّث بيانات المنتج…';
    if (names.includes('get_product_info') || names.includes('find_products')) return 'بدوّر في المنتجات…';
    if (names.includes('get_reservations')) return 'بدوّر على الحجوزات…';
    if (names.includes('get_order_info')) return 'بدوّر على تفاصيل الطلب…';
    if (names.includes('get_orders')) return 'بحسب الأوردرات…';
    if (names.includes('get_customer_info') || names.includes('find_customers')) return 'بجيب بيانات العميل…';
    if (names.includes('get_sales_report') || names.includes('get_pages_analytics')) return 'براجع أرقام المبيعات…';
    if (names.includes('get_products')) return 'بدوّر في المنتجات…';
    if (names.includes('find_collections')) return 'بدوّر على الكولكشن…';
    if (names.includes('open_page')) return 'بفتح الصفحة…';
    if (names.includes('logout')) return 'بخرجك دلوقتي…';
    return 'بدوّر على الإجابة…';
}

export default function VoiceMode({ open, onClose, onNavigate, onTranscript, onSessionEnd }) {
    const [status, setStatus] = useState('idle');
    const [caption, setCaption] = useState('');
    const [error, setError] = useState(null);
    const [endingSummary, setEndingSummary] = useState(null);

    const pcRef = useRef(null);
    const dcRef = useRef(null);
    const streamRef = useRef(null);
    const audioRef = useRef(null);
    const assistantBufRef = useRef('');
    const closedRef = useRef(false);
    const greetedRef = useRef(false);
    const speakingRef = useRef(false);
    const busyRef = useRef(false);
    const toolsRunningRef = useRef(false);
    const bargeInUntilRef = useRef(0);
    const awaitingAudioStopRef = useRef(false);
    const endedOnceRef = useRef(false);
    const usagesRef = useRef([]);
    const modelRef = useRef('gpt-realtime-mini');
    const ringtoneRef = useRef(null);
    const onNavigateRef = useRef(onNavigate);
    const onTranscriptRef = useRef(onTranscript);
    const onSessionEndRef = useRef(onSessionEnd);

    useEffect(() => {
        onNavigateRef.current = onNavigate;
    }, [onNavigate]);
    useEffect(() => {
        onTranscriptRef.current = onTranscript;
    }, [onTranscript]);
    useEffect(() => {
        onSessionEndRef.current = onSessionEnd;
    }, [onSessionEnd]);

    const setMicEnabled = (enabled) => {
        streamRef.current?.getAudioTracks?.().forEach((track) => {
            track.enabled = enabled;
        });
    };

    const setRemoteAudioMuted = (muted) => {
        if (audioRef.current) {
            audioRef.current.muted = Boolean(muted);
        }
    };

    const beginSpeaking = () => {
        if (Date.now() < bargeInUntilRef.current) return;
        speakingRef.current = true;
        busyRef.current = true;
        stopStatusSpeech();
        // Keep mic open so the user can barge in / skip the current reply
        setMicEnabled(true);
        setRemoteAudioMuted(false);
        setStatus('speaking');
    };

    const endSpeaking = () => {
        speakingRef.current = false;
        if (!toolsRunningRef.current) {
            busyRef.current = false;
        }
        awaitingAudioStopRef.current = false;
        setMicEnabled(true);
        setRemoteAudioMuted(false);
        setStatus('listening');
        setCaption('سامعك… اتكلم (تقدر تقاطعه وهو بيتكلم)');
    };

    const stopRingtone = () => {
        try {
            ringtoneRef.current?.stop();
        } catch (_) {
            /* ignore */
        }
        ringtoneRef.current = null;
    };

    const cleanupMedia = () => {
        stopRingtone();
        try {
            dcRef.current?.close();
        } catch (_) {
            /* ignore */
        }
        try {
            pcRef.current?.close();
        } catch (_) {
            /* ignore */
        }
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
        if (audioRef.current) audioRef.current.srcObject = null;
        pcRef.current = null;
        dcRef.current = null;
        streamRef.current = null;
    };

    const finishCall = () => {
        if (endedOnceRef.current) return;
        endedOnceRef.current = true;
        closedRef.current = true;
        cleanupMedia();
        const usage = priceRealtimeUsages(usagesRef.current, modelRef.current);
        setEndingSummary(usage);
        if (typeof onSessionEndRef.current === 'function') {
            onSessionEndRef.current(usage);
        }
        setTimeout(() => {
            setEndingSummary(null);
            onClose?.();
        }, 1600);
    };

    useEffect(() => {
        if (!open) return undefined;
        closedRef.current = false;
        greetedRef.current = false;
        speakingRef.current = false;
        busyRef.current = false;
        toolsRunningRef.current = false;
        awaitingAudioStopRef.current = false;
        endedOnceRef.current = false;
        usagesRef.current = [];
        setEndingSummary(null);
        let cancelled = false;

        const setVadEnabled = (enabled) => {
            sendEvent(dcRef.current, {
                type: 'session.update',
                session: {
                    type: 'realtime',
                    audio: {
                        input: {
                            turn_detection: enabled
                                ? {
                                      type: 'server_vad',
                                      threshold: 0.72,
                                      prefix_padding_ms: 250,
                                      silence_duration_ms: 700,
                                      create_response: true,
                                      interrupt_response: true,
                                  }
                                : null,
                        },
                    },
                },
            });
        };

        const interruptAssistantSpeech = () => {
            // Skip whatever Tabexawy is saying and listen to the new topic
            bargeInUntilRef.current = Date.now() + 700;
            sendEvent(dcRef.current, { type: 'response.cancel' });
            sendEvent(dcRef.current, { type: 'output_audio_buffer.clear' });
            setRemoteAudioMuted(true);
            speakingRef.current = false;
            awaitingAudioStopRef.current = false;
            if (!toolsRunningRef.current) {
                busyRef.current = false;
            }
            assistantBufRef.current = '';
            setMicEnabled(true);
            setStatus('listening');
            setCaption('قاطعتَه · سامعك…');
            setTimeout(() => {
                if (!closedRef.current && !cancelled) setRemoteAudioMuted(false);
            }, 250);
        };

        const maybeGreet = () => {
            if (greetedRef.current || cancelled || closedRef.current) return;
            if (!dcRef.current || dcRef.current.readyState !== 'open') return;
            greetedRef.current = true;
            stopRingtone();
            busyRef.current = true;
            speakingRef.current = true;
            setMicEnabled(true);
            setVadEnabled(true);
            setStatus('speaking');
            setCaption('اتصلت · بيسلّم عليك… (تقدر تقاطعه)');
            sendEvent(dcRef.current, {
                type: 'response.create',
                response: {
                    instructions:
                        'جملة ترحيب قصيرة بالعامية المصرية فقط: "أنا Tabexawy، إزاي أقدر أساعدك؟" وبعدين اسكت. متزودش ومتسألش أكتر من كده.',
                },
            });
        };

        const handleServerEvent = async (event) => {
            const type = event?.type;
            if (!type) return;

            if (type === 'response.created') {
                busyRef.current = true;
                if (!toolsRunningRef.current) {
                    setMicEnabled(true);
                    setVadEnabled(true);
                }
            }

            if (type === 'output_audio_buffer.started' || type === 'response.output_audio.delta') {
                beginSpeaking();
                awaitingAudioStopRef.current = true;
            }

            if (type === 'output_audio_buffer.stopped') {
                awaitingAudioStopRef.current = false;
                if (!busyRef.current && !toolsRunningRef.current) {
                    endSpeaking();
                    setVadEnabled(true);
                } else if (!toolsRunningRef.current) {
                    speakingRef.current = false;
                }
            }

            if (type === 'input_audio_buffer.speech_started') {
                if (speakingRef.current) {
                    interruptAssistantSpeech();
                    return;
                }
                if (toolsRunningRef.current || busyRef.current) {
                    sendEvent(dcRef.current, { type: 'input_audio_buffer.clear' });
                    return;
                }
                setStatus('listening');
                setCaption('سامعك…');
                assistantBufRef.current = '';
            }

            if (type === 'input_audio_buffer.speech_stopped') {
                if (speakingRef.current || toolsRunningRef.current || busyRef.current) return;
                setCaption('بيفكّر في رد…');
            }

            if (type === 'conversation.item.input_audio_transcription.completed') {
                const text = String(event.transcript || '').trim();
                if (text && typeof onTranscriptRef.current === 'function') {
                    onTranscriptRef.current({ role: 'user', text });
                }
            }

            if (
                type === 'response.output_audio_transcript.delta' ||
                type === 'response.audio_transcript.delta'
            ) {
                const delta = event.delta || '';
                if (delta) {
                    assistantBufRef.current += delta;
                    beginSpeaking();
                    setCaption(assistantBufRef.current.slice(-140));
                }
            }

            if (
                type === 'response.output_audio_transcript.done' ||
                type === 'response.audio_transcript.done'
            ) {
                const text = String(event.transcript || assistantBufRef.current || '').trim();
                assistantBufRef.current = '';
                if (text && typeof onTranscriptRef.current === 'function') {
                    onTranscriptRef.current({ role: 'assistant', text });
                }
            }

            if (type === 'response.done') {
                if (event.response?.usage) {
                    usagesRef.current.push(event.response.usage);
                }

                const outputs = event.response?.output || [];
                const toolCalls = outputs.filter((item) => item.type === 'function_call');
                if (!toolCalls.length) {
                    busyRef.current = false;
                    toolsRunningRef.current = false;
                    if (!awaitingAudioStopRef.current) {
                        endSpeaking();
                        setVadEnabled(true);
                    }
                    return;
                }

                toolsRunningRef.current = true;
                busyRef.current = true;
                speakingRef.current = false;
                setMicEnabled(false);
                setVadEnabled(false);
                setStatus('tools');
                const statusLine = statusPhraseForTools(toolCalls);
                setCaption(statusLine);

                let reservationsOnce = null;
                for (const call of toolCalls) {
                    const result =
                        call.name === 'get_reservations'
                            ? (reservationsOnce ||= await executeDashboardTool(
                                  call.name,
                                  call.arguments || '{}',
                              ))
                            : await executeDashboardTool(call.name, call.arguments || '{}');
                    if (typeof onNavigateRef.current === 'function') {
                        const path =
                            result?.navigate?.path ||
                            result?.path ||
                            (result?.auto_open && result?.ok ? result.open_path : null);
                        if (path && result?.ok) {
                            onNavigateRef.current(path, result.navigate?.label || result.label || path);
                        }
                    }
                    sendEvent(dcRef.current, {
                        type: 'conversation.item.create',
                        item: {
                            type: 'function_call_output',
                            call_id: call.call_id,
                            output: JSON.stringify(result ?? {}),
                        },
                    });
                }

                toolsRunningRef.current = false;
                setMicEnabled(true);
                setVadEnabled(true);
                setCaption('بهيّأ الإجابة…');

                sendEvent(dcRef.current, {
                    type: 'response.create',
                    response: {
                        instructions:
                            'رد واحد بالعامية المصرية: جاوب على السؤال المطلوب بس من نتيجة الأدوات. لو حجوزات قول العدد ومختصر. لو تفاصيل أوردر اقرأ الخلاصة. ممنوع تقول شوف الصفحة. متبدأش رد تاني ومتتكلمش فوق نفسك.',
                    },
                });
            }

            if (type === 'error') {
                console.error('Realtime error', event);
                setCaption(event.error?.message || 'Voice error');
                busyRef.current = false;
                toolsRunningRef.current = false;
                endSpeaking();
                setVadEnabled(true);
            }
        };

        const start = async () => {
            setStatus('connecting');
            setError(null);
            setCaption('بيرنّ… بيوصل المكالمة');

            const ringtone = createCallRingtone();
            ringtoneRef.current = ringtone;
            try {
                await ringtone.start();
            } catch (_) {
                /* autoplay policies — ignore */
            }

            try {
                const tokenResponse = await fetch(aiserverbaselink + '/instowner/ai/realtime/session', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        ...aiInstituteHeaders(),
                    },
                });
                const tokenPayload = await tokenResponse.json().catch(() => ({}));
                if (!tokenResponse.ok || !tokenPayload.value) {
                    throw new Error(tokenPayload.error || 'Unable to start voice mode.');
                }

                if (cancelled || closedRef.current) return;

                modelRef.current = tokenPayload.model || 'gpt-realtime-mini';
                const ephemeralKey = tokenPayload.value;

                const pc = new RTCPeerConnection();
                pcRef.current = pc;

                const audioEl = document.createElement('audio');
                audioEl.autoplay = true;
                audioRef.current = audioEl;
                pc.ontrack = (event) => {
                    audioEl.srcObject = event.streams[0];
                };

                setCaption('بيرنّ… بيستأذن المايك');
                const ms = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    },
                });
                if (cancelled || closedRef.current) {
                    ms.getTracks().forEach((t) => t.stop());
                    stopRingtone();
                    return;
                }
                streamRef.current = ms;
                // Start muted until greeting finishes — stops echo loops at call start
                ms.getAudioTracks().forEach((track) => {
                    track.enabled = false;
                });
                ms.getTracks().forEach((track) => pc.addTrack(track, ms));

                const dc = pc.createDataChannel('oai-events');
                dcRef.current = dc;
                dc.addEventListener('message', async (event) => {
                    let payload;
                    try {
                        payload = JSON.parse(event.data);
                    } catch (_) {
                        return;
                    }
                    await handleServerEvent(payload);
                });

                setCaption('بيرنّ… بيوصل الخط');
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                await waitForIceGathering(pc);

                const localSdp = pc.localDescription?.sdp;
                if (!localSdp || localSdp.length < 50) {
                    throw new Error('Microphone SDP offer was empty. Check mic permissions and try again.');
                }

                const sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
                    method: 'POST',
                    body: localSdp,
                    headers: {
                        Authorization: `Bearer ${ephemeralKey}`,
                        'Content-Type': 'application/sdp',
                    },
                });
                const answerSdp = await sdpResponse.text();
                if (!sdpResponse.ok) {
                    let message = 'Unable to connect voice session.';
                    try {
                        const errBody = JSON.parse(answerSdp);
                        message = errBody.error?.message || errBody.message || message;
                    } catch (_) {
                        if (answerSdp) message = answerSdp.slice(0, 200);
                    }
                    throw new Error(message);
                }

                await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

                // Single greet after channel is ready (ringtone stops in maybeGreet)
                const greetWhenReady = () => {
                    if (cancelled || closedRef.current) return;
                    if (dc.readyState === 'open') {
                        maybeGreet();
                        return;
                    }
                    dc.addEventListener(
                        'open',
                        () => {
                            maybeGreet();
                        },
                        { once: true },
                    );
                };
                greetWhenReady();
            } catch (err) {
                stopRingtone();
                if (cancelled || closedRef.current) return;
                console.error(err);
                setError(err);
                setStatus('error');
                setCaption(err.message || 'Voice mode failed');
            }
        };

        start();

        return () => {
            cancelled = true;
            closedRef.current = true;
            stopStatusSpeech();
            cleanupMedia();
        };
    }, [open]);

    if (!open) return null;

    return (
        <div className="voice-overlay" role="dialog" aria-label="Talk with Tabexawy">
            <div className="voice-card">
                <div className={`voice-orb ${status}`} aria-hidden="true">
                    <span />
                    <span />
                    <span />
                </div>
                <div className="voice-title">مكالمة مع Tabexawy</div>
                <div className="voice-caption">
                    {endingSummary
                        ? `انتهت المكالمة · ${usageFooterText(endingSummary)}`
                        : caption || 'Ready'}
                </div>
                {error && <div className="voice-error">{error.message}</div>}
                {!endingSummary && (
                    <button type="button" className="voice-end" onClick={finishCall}>
                        إنهاء المكالمة
                    </button>
                )}
            </div>
        </div>
    );
}
